export default class ProductItemVideoHover {
    static init(): void {
        new ProductVideoHandler();
    }
}

class ProductVideoHandler {
    private observerOptions: IntersectionObserverInit = {
        root: null,
        rootMargin: '50px',
        threshold: 0.1,
    };

    private videoObserver: IntersectionObserver;

    constructor() {
        this.videoObserver = new IntersectionObserver(
            (entries) => this.handleIntersection(entries),
            this.observerOptions,
        );

        this.initVideoItems();
        this.observeVideoItems();
    }

    private initVideoItems(): void {
        const videoItems = document.querySelectorAll<HTMLElement>('.category-item--with-video');

        videoItems.forEach((item) => {
            const video = item.querySelector<HTMLVideoElement>('.category-item__video');
            const img = item.querySelector<HTMLImageElement>('.category-item__img img');
            const imgContainer = item.querySelector<HTMLElement>('.category-item__img');

            if (!video || !img || !imgContainer) return;

            // Hover events
            item.addEventListener('mouseenter', () => this.handleMouseEnter(video, img, imgContainer));
            item.addEventListener('mouseleave', () => this.handleMouseLeave(video, img, imgContainer));

            // Error handling
            video.addEventListener('error', () => this.handleVideoError(imgContainer));
            video.addEventListener('loadstart', () => this.handleVideoLoadStart(imgContainer));
            video.addEventListener('canplay', () => this.handleVideoCanPlay(imgContainer));
        });
    }

    private observeVideoItems(): void {
        const videoItems = document.querySelectorAll<HTMLElement>('.category-item--with-video');
        videoItems.forEach((item) => this.videoObserver.observe(item));
    }

    private handleMouseEnter(video: HTMLVideoElement, _img: HTMLImageElement, container: HTMLElement): void {
        if (container.classList.contains('category-item__img--error')) {
            return; // Don't try to play if video failed to load
        }

        container.classList.add('category-item__img--loading');

        // Try to play video
        const playPromise: Promise<void> | undefined = video.play();

        if (playPromise !== undefined) {
            playPromise
                .then(() => {
                    video.classList.add('is-playing');
                    container.classList.remove('category-item__img--loading');
                })
                .catch((error: Error) => {
                    console.log('Video play failed:', error);
                    container.classList.remove('category-item__img--loading');
                    container.classList.add('category-item__img--error');
                });
        }
    }

    private handleMouseLeave(video: HTMLVideoElement, _img: HTMLImageElement, container: HTMLElement): void {
        video.pause();
        video.currentTime = 0;
        video.classList.remove('is-playing');
        container.classList.remove('category-item__img--loading');
    }

    private handleVideoError(container: HTMLElement): void {
        container.classList.remove('category-item__img--loading');
        container.classList.add('category-item__img--error');
        console.log('Video failed to load');
    }

    private handleVideoLoadStart(container: HTMLElement): void {
        container.classList.add('category-item__img--loading');
    }

    private handleVideoCanPlay(container: HTMLElement): void {
        container.classList.remove('category-item__img--loading', 'category-item__img--error');
    }

    private handleIntersection(entries: IntersectionObserverEntry[]): void {
        entries.forEach((entry: IntersectionObserverEntry) => {
            const video = entry.target.querySelector<HTMLVideoElement>('.category-item__video');

            if (entry.isIntersecting) {
                // Preload video when item comes into view
                if (video && video.preload === 'none') {
                    video.preload = 'metadata';
                }
            } else {
                // Reset video when out of view
                if (video) {
                    video.pause();
                    video.currentTime = 0;
                    video.classList.remove('is-playing');
                }
            }
        });
    }

    // Method to cleanup observers when component is destroyed
    public destroy(): void {
        this.videoObserver.disconnect();
    }
}